import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Sheet,
    Table,
    Typography
} from '@mui/joy';
import AssessmentIcon from '@mui/icons-material/Assessment';
import axiosConfig from '../util/axiosConfig';

const tableSheetSx = {
    width: '100%',
    maxWidth: '100%',
    borderRadius: 'sm',
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: 360,
    WebkitOverflowScrolling: 'touch',
    border: '1px solid',
    borderColor: 'divider'
};

const tableSx = {
    width: 'max-content',
    minWidth: '100%',
    tableLayout: 'fixed',
    '& tbody tr:nth-of-type(even)': { backgroundColor: '#f9f9f9' },
    '& tbody tr:hover': { backgroundColor: '#fff5ed' },
    '& thead': { position: 'sticky', top: 0, zIndex: 10 },
    '& thead th': {
        backgroundColor: '#eb7a24',
        color: 'white',
        fontWeight: 'bold',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        borderBottom: '2px solid #e55125',
        whiteSpace: 'nowrap'
    },
    '& td, & th': {
        padding: '12px',
        border: '1px solid',
        borderColor: 'divider',
        verticalAlign: 'middle',
        overflow: 'hidden'
    }
};

/**
 * Dashboard preview of assessments with status "Scheduled".
 * Renders only when at least one scheduled assessment exists.
 */
const ScheduledAssessmentPreview = () => {
    const [loading, setLoading] = useState(true);
    const [assessments, setAssessments] = useState([]);

    const shouldRender = useMemo(() => !loading && assessments.length > 0, [loading, assessments.length]);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axiosConfig.get('/students/assessment-status');
            const allAssessments = response.data?.assessments || [];
            const todayIso = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD, local time
            setAssessments(allAssessments.filter((assessment) => (
                assessment.statusCode === 0 && assessment.rawDate && assessment.rawDate >= todayIso
            )));
        } catch (error) {
            console.error('Error fetching scheduled assessments:', error);
            setAssessments([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    if (!shouldRender) {
        return null;
    }

    return (
        <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AssessmentIcon sx={{ color: '#eb7a24' }} />
                    <Typography level="title-lg" sx={{ color: '#eb7a24' }}>
                        {assessments.length > 1
                            ? `${assessments.length} Assessments Scheduled`
                            : 'Assessment Scheduled'}
                    </Typography>
                </Box>
                <Sheet variant="outlined" sx={tableSheetSx}>
                    <Table aria-label="scheduled assessments preview table" stickyHeader sx={tableSx}>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Module</th>
                                <th>Faculty</th>
                            </tr>
                        </thead>
                        <tbody>
                            {assessments.map((assessment, index) => (
                                <tr key={index}>
                                    <td>{assessment.date || '-'}</td>
                                    <td>{assessment.module || '-'}</td>
                                    <td>{assessment.faculty || '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Sheet>
            </CardContent>
        </Card>
    );
};

export default ScheduledAssessmentPreview;
